import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { getPaymentsPerCategory } from "common/services/payments.service";
import useQueryState from "Components/Hooks/UseQueryState";
import moment from "moment";
import { FilterType, defaultFilter } from "../Filters";
import Skeleton from "react-loading-skeleton";
import useChartColors from "Components/Common/useChartColors";

type PaymentSummary = {
  category: string;
  totalamount: number;
  totalcount: number;
};

const groupData = (payments: PaymentSummary[]) => {
  const totalCounts = payments.map((payment) => Number(payment.totalcount));
  const categories = payments.map((payment) => payment.category);

  return { totalCounts, categories };
};

const ContractsPerPaymentCategory = () => {
  const [filter] = useQueryState<FilterType>("filter", defaultFilter);

  const [payments, setPayments] = useState<PaymentSummary[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsPerCategory({
          mdaId: filter?.mda?.value || undefined,
          regionId: filter?.region?.value || undefined,
          positionTypeId: filter?.positionType?.value || undefined,
          agencyId: filter?.agency?.value || undefined,
          paymentCategoryId: filter?.paymentCategory?.value || undefined,
          thematicAreaId: filter?.thematicArea?.value || undefined,
          sectorId: filter?.sector?.value || undefined,
          startDate: filter?.dateRange?.fromDate
            ? moment(filter.dateRange.fromDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
          endDate: filter?.dateRange?.toDate
            ? moment(filter.dateRange.toDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    filter?.agency?.value,
    filter?.dateRange?.fromDate,
    filter?.dateRange?.toDate,
    filter.mda,
    filter?.positionType?.value,
    filter?.paymentCategory?.value,
    filter?.region?.value,
    filter?.sector?.value,
    filter?.thematicArea?.value,
  ]);

  const { totalCounts, categories } = useMemo(
    () => groupData(payments),
    [payments]
  );

  const chartPieBasicColors = useChartColors("contracts_per_payment_category");
  const series = totalCounts;
  var options: any = {
    chart: {
      height: 300,
      type: "pie",
    },
    labels: categories,
    legend: {
      position: "bottom",
    },
    dataLabels: {
      dropShadow: {
        enabled: false,
      },
    },
    colors: chartPieBasicColors,
    tooltip: {
      enabled: true, // Enable tooltips
      y: {
        formatter: function (value: number) {
          return value.toLocaleString() + " Contracts"; // Format the tooltip value
        },
        title: {
          formatter: function (seriesName: string) {
            return `${seriesName}:`; // Customize the title of the tooltip
          },
        },
      },
    },
  };

  return (
    <Card className="card-height-100">
      <CardHeader>
        <h4 className="card-title mb-0">Contracts per Payment Category</h4>
      </CardHeader>

      <CardBody className="d-flex flex-column align-items-center">
        {loading ? (
          <>
            <Skeleton circle width={220} height={220} />
            <div className="d-flex justify-content-center gap-3 mt-3 w-100">
              <Skeleton width={80} height={16} />
              <Skeleton width={80} height={16} />
              <Skeleton width={80} height={16} />
              <Skeleton width={80} height={16} />
            </div>
          </>
        ) : (
          <ReactApexChart
            dir="ltr"
            className="apex-charts"
            series={series}
            options={options}
            type="pie"
            height={300}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default ContractsPerPaymentCategory;
